import { formatInTimeZone } from 'date-fns-tz';
import { SnapshotAlarmLogReturn, TraceHistory } from '../../../types';
import { AlarmLogItemType } from '../../../types/models/SnapshotAlarmLogItem';
import { SnapshotAlarmLogReturnWithStop } from '../types';

export const combineMessageFields = (
  logItem: SnapshotAlarmLogReturnWithStop,
  inImperial: boolean | null | undefined,
  dateTimeFormat: string,
  timeZone: string
) => {
  let message =
    formatInTimeZone(new Date(logItem.time), timeZone, dateTimeFormat) +
    '\n' +
    'Alarm: ' +
    logItem.alarm.name;

  for (let i = 0; i < logItem.snapshot_alarm_log_items.length; i++) {
    if (logItem.snapshot_alarm_log_items[i].type === AlarmLogItemType.STOP) {
      if (logItem.stopTime)
        message +=
          '\n\n' +
          formatInTimeZone(
            new Date(logItem.stopTime),
            timeZone,
            'MM/dd/yyyy hh:mm:ss aa'
          ) +
          '\n';
      message += logItem.snapshot_alarm_log_items[i].message.trim();
    } else {
      message += '\n\n' + logItem.snapshot_alarm_log_items[i].message.trim();
    }
  }
  message += inImperial
    ? '\n\n' + logItem.weather_message_imperial
    : '\n\n' + logItem.weather_message_metric;

  return message.trim();
};

export const joinAlarmStopsToStarts = (response: SnapshotAlarmLogReturn[]) => {
  const responseWithJoinedStops: SnapshotAlarmLogReturnWithStop[] = [];
  let index = 0;
  const alarmIdIndexPairs: { [key: number]: number } = {};
  // Iterate backward through the logs (i.e., from old to new)
  for (let i = response.length - 1; i >= 0; i--) {
    if (
      !response[i]?.snapshot_alarm_log_items ||
      response[i]?.snapshot_alarm_log_items.length === 0
    )
      continue;
    // If we've seen this alarm ID before and the timestamp is associated with a stopped alarm...
    if (
      alarmIdIndexPairs.hasOwnProperty(response[i].alarm_id) &&
      response[i].snapshot_alarm_log_items[0]?.type === AlarmLogItemType.STOP
    ) {
      // Add the log item to the last seen alarm timestamp
      const associatedStartAlarm =
        responseWithJoinedStops[alarmIdIndexPairs[response[i].alarm_id]];
      associatedStartAlarm.snapshot_alarm_log_items.push(
        response[i].snapshot_alarm_log_items[0]
      );
      associatedStartAlarm.stopTime = response[i].time;
      delete alarmIdIndexPairs[response[i].alarm_id];
      // Otherwise, remember where we last inserted a log with this alarm ID
    } else {
      // Don't save stop alarms as independent entries
      if (
        response[i].snapshot_alarm_log_items[0].type !== AlarmLogItemType.STOP
      ) {
        responseWithJoinedStops.push(response[i]);
        alarmIdIndexPairs[response[i].alarm_id] = index;
        index += 1;
      }
    }
  }
  responseWithJoinedStops.reverse();
  return responseWithJoinedStops;
};

export const getLogTime = (
  alarmLog: SnapshotAlarmLogReturnWithStop,
  alarmStop: boolean
) => {
  return alarmStop && alarmLog.stopTime ? alarmLog.stopTime : alarmLog.time;
};

export const getLogIndexForOneSnapshot = (
  alarmLog: SnapshotAlarmLogReturnWithStop,
  alarmStop: boolean
) => {
  return alarmStop ? alarmLog.snapshot_alarm_log_items.length - 1 : 0;
};

export const getLogIndexForManySnapshots = (
  alarmLog: SnapshotAlarmLogReturnWithStop,
  alarmStop: boolean,
  selectedLogItem: string
) => {
  let logIndex;
  if (alarmStop) {
    const currentLogItem = alarmLog.snapshot_alarm_log_items.find(
      (item) => item.id === Number(selectedLogItem)
    );
    for (const item of alarmLog.snapshot_alarm_log_items) {
      if (
        item.snapshot_id === currentLogItem?.snapshot_id &&
        item.analyzer_id === currentLogItem.analyzer_id
      ) {
        logIndex = item.id;
      }
    }
  } else {
    logIndex = Number(selectedLogItem);
  }
  return logIndex;
};

export function removeDuplicateSnapshots<
  ItemType extends { snapshot_id: number }
>(items: ItemType[]) {
  const snapshotIdsInLogItems: number[] = [];
  const result: ItemType[] = [];
  for (const item of items) {
    if (snapshotIdsInLogItems.includes(item.snapshot_id)) continue;
    snapshotIdsInLogItems.push(item.snapshot_id);
    result.push(item);
  }
  return result;
}

// This function uses a linear search over the entire trace array;
//  if needed, improve performance
export const findClosestTraceIndex = (
  traces: TraceHistory[],
  timestamp: string
) => {
  if (isNaN(Date.parse(timestamp))) return 0;
  let timeDelta = Number.MAX_VALUE;
  let centerIndex = 0;
  traces.forEach((trace, index) => {
    const currentDelta = Math.abs(
      Date.parse(trace.time) - Date.parse(timestamp)
    );
    if (currentDelta < timeDelta) {
      centerIndex = index;
      timeDelta = currentDelta;
    }
  });
  return centerIndex;
};
